/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { sharedAdminuiAppApi } from '../../../ServiceFactory';
import { ellipsify, getCurrentPage, getNextPage, isEmptyOrNull } from '../helpers';
import { useStore } from '../store';
import { Contract, ListType } from '../types';

const useFetchContracts = (
	tenantIds: string[],
	appVariantIds: string[],
	search?: string
): UseInfiniteQueryResult<Contract[]> => {
	const { setCurrentListPage, currentContractsPage } = useStore();
	return useInfiniteQuery<Contract[]>(
		[ListType.CONTRACT, { tenantIds, appVariantIds, search }],
		({ pageParam = 0 }) => {
			return sharedAdminuiAppApi()
				.getContractsByTenantOrAppVariant({ count: 500, tenantIds, appVariantIds, page: pageParam, search })
				.then(response => response)
				.then(data => {
					const { content, page } = data;
					const { totalPages, number: responsePage } = page || {};
					const processedItems =
						content?.map(item => {
							const newItem = { ...item } as Contract;
							const formattedDate = item.created ? new Date(item.created).toLocaleString('de-DE') : '';
							newItem.name = isEmptyOrNull(item?.notes)
								? `Nutzungsvertrag ${item.id} von ${formattedDate}`
								: `${ellipsify(item.notes, 40)} von ${formattedDate}`;
							return newItem;
						}) || [];
					const currentPage = getCurrentPage(responsePage, totalPages);
					setCurrentListPage(ListType.CONTRACT, currentPage);
					return processedItems;
				});
		},
		{
			getNextPageParam: () => getNextPage(currentContractsPage)
		}
	);
};

export default useFetchContracts;
